import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// REDUX Imports
import {
  changePassword,
  resetChangePassword,
  setMultipleEntity,
  setShowReloginModalFalse,
} from "../../../redux/features/login/login";
import {
  xpentraLogout,
  xpentraLogoutSliceReset,
} from "../../../redux/features/logout/logout";
import { logout } from "../../../redux/features/refreshToken/refreshToken";

// UTLIS Imports
import PrimaryLargeButton from "../../../components/_utils/CommonButtons/PrimaryLargeButton";
import PasswordInputField from "../../../components/_utils/CommonInputFields/PasswordInputField";
import CommonToast from "../../../components/_utils/CommonToast";
import CommonAlertCard from "../../../components/_utils/CommonAlertCard";
import ICONS from "../../../assets/Icons";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

const ChangePassword = () => {
  // useDispatch
  const dispatch = useDispatch();

  // useNavigate
  const navigate = useNavigate();

  // useState
  const [buttonLoading, setButtonLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    currentPasswordValue: "",
    newPasswordValue: "",
    confirmPasswordValue: "",
  });

  // useSelector
  const { changePasswordError } = useSelector((state) => state.login);

  const { xpentraLogoutLoading, xpentraLogoutData, xpentraLogoutError } =
    useSelector((state) => state?.xpentraLogoutSlice);

  // useCallback
  const handleConfirmChangePassword = useCallback(async () => {
    if (
      passwordValues?.currentPasswordValue === "" ||
      passwordValues?.newPasswordValue === "" ||
      passwordValues?.confirmPasswordValue === "" ||
      changePasswordError ||
      passwordError
    ) {
      return;
    } else if (
      passwordValues?.currentPasswordValue?.length >
        GlobalConfiguration.lengthCheckZero &&
      passwordValues?.newPasswordValue !== passwordValues?.confirmPasswordValue
    ) {
      //to check whether the new password and the confirm password matches.
      setPasswordError(true);
    } else if (
      passwordValues?.currentPasswordValue?.length >
        GlobalConfiguration.lengthCheckZero &&
      passwordValues?.newPasswordValue?.length >
        GlobalConfiguration.lengthCheckTwo &&
      passwordValues?.confirmPasswordValue?.length >
        GlobalConfiguration.lengthCheckZero
    ) {
      setButtonLoading(true);
      const params = {
        oldPassword: passwordValues?.currentPasswordValue,
        newPassword: passwordValues?.newPasswordValue,
        confirmPassword: passwordValues?.confirmPasswordValue,
        setButtonLoading: setButtonLoading,
      };

      //change the password of logedIn user.
      const response = await dispatch(changePassword(params));

      if (response?.payload?.status) {
        CommonToast({
          text: "Password reset successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        setPasswordValues({
          currentPasswordValue: "",
          newPasswordValue: "",
          confirmPasswordValue: "",
        });
        //User logout API call.
        dispatch(xpentraLogout());
        setButtonLoading(false);
      }
    }
  }, [passwordValues, changePasswordError, passwordError]);

  const handleEnterKey = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      } else {
        handleConfirmChangePassword();
      }
    }
  };

  // useEffect
  useEffect(() => {
    if (!xpentraLogoutLoading && xpentraLogoutData?.status) {
      //if the user logout successfully { clear all the session storage and user data }
      dispatch(setMultipleEntity());
      dispatch(setShowReloginModalFalse());
      dispatch(logout());
      dispatch(xpentraLogoutSliceReset());
      navigate("/login");
      sessionStorage.clear();
      setButtonLoading(false);
    } else if (
      !xpentraLogoutLoading &&
      !xpentraLogoutData &&
      xpentraLogoutError?.isErrorStatus
    ) {
      //if not check the token of user is valid OR not

      dispatch(xpentraLogoutSliceReset());
      setButtonLoading(false);
    }
  }, [
    xpentraLogoutLoading,
    xpentraLogoutData,
    xpentraLogoutError,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    document.addEventListener("keydown", handleEnterKey);
    return () => {
      //when component unmount
      // if (window.location.pathname !== "/settings/my_profile/change_password") {
      //   dispatch(resetChangePassword());
      // }
      document.removeEventListener("keydown", handleEnterKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordValues, changePasswordError, passwordError]);

  return (
    <div
      className="h-screen flex justify-center"
      style={{
        backgroundImage:
          "url('https://q2pay.in/new_assets/graphics/login/backgrond_for_login.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {" "}
      <div className="w-full h-full grid place-items-center  Qlg:p-padding-base p-padding-sm">
        <div className="w-full Qsmd:w-fit rounded-border-radius-md p-[var(--border)] bg-[linear-gradient(154.44deg,rgba(97,117,153,0.5)_16.18%,rgba(100,100,100,0.5)_100.4%)] shadow-[0px_4px_4px_0px_#00000033]">
          <div className="Qsmd:p-padding-xl px-padding-sm py-padding-lg rounded-border-radius-md bg-primary-bg Qsmd:w-[var(--login-card-height)] w-full  h-fit">
            <div className="flex flex-col gap-gap-xl items-center">
              <div className="w-xpentra-logo-width-login h-[var(--gap-lg)]">
                <img
                  src={ICONS?.XPENTRA_WHITE}
                  alt=""
                />
              </div>

              <div className="flex flex-col  items-start justify-between w-full ">
                <CommonAlertCard
                  texts={
                    "After changing the password, you’ll be logged out and need to log in again."
                  }
                />
                <h4 className="text-font-sm font-semibold font-sans text-text-primary mb-margin-xs mt-margin-lg ">
                  Set new password
                </h4>
                <p className=" text-font-xs font-normal font-sans text-text-primary mb-margin-lg ">
                  Enter current password, then set and confirm your new
                  password.
                </p>

                <div className="w-full flex flex-col gap-gap-md">
                  <PasswordInputField
                    bgColor="var(--primary-bg)"
                    fgColor="var(--text-secondary)"
                    isFullWidth={true}
                    placeholder="Current Password"
                    variant={
                      changePasswordError
                        ? "error"
                        : passwordValues?.oldPassword
                          ? "typing"
                          : "default"
                    }
                    fontKind="big"
                    value={passwordValues?.currentPasswordValue}
                    onInput={(event) => {
                      setPasswordValues((prev) => {
                        return {
                          ...prev,
                          currentPasswordValue: event.target.value.trim(),
                        };
                      });
                      dispatch(resetChangePassword());
                      setPasswordError(false);
                    }}
                  />

                  <PasswordInputField
                    bgColor="var(--primary-bg)"
                    fgColor="var(--text-secondary)"
                    isFullWidth={true}
                    placeholder="New password (at least 3 char)"
                    variant={passwordError ? "error" : "default"}
                    fontKind="big"
                    value={passwordValues?.newPasswordValue}
                    onInput={(event) => {
                      setPasswordValues((prev) => {
                        return {
                          ...prev,
                          newPasswordValue: event.target.value.trim(),
                        };
                      });
                      dispatch(resetChangePassword());
                      setPasswordError(false);
                    }}
                  />

                  <div className="w-full">
                    <PasswordInputField
                      bgColor="var(--primary-bg)"
                      fgColor="var(--text-secondary)"
                      isFullWidth={true}
                      placeholder="Confirm new password"
                      variant={passwordError ? "error" : "default"}
                      fontKind="big"
                      value={passwordValues?.confirmPasswordValue}
                      onInput={(event) => {
                        setPasswordValues((prev) => {
                          return {
                            ...prev,
                            confirmPasswordValue: event.target.value.trim(),
                          };
                        });
                        dispatch(resetChangePassword());
                        setPasswordError(false);
                      }}
                    />
                    {passwordError && (
                      <p className="text-failed text-xs font-semibold mt-margin-xs ml-margin-xs">
                        Passwords do not match.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <PrimaryLargeButton
                btnType="button"
                btnText="Change password"
                hoverOption="black"
                enabledTextColor="var(--text-primary)"
                disabledTextColor="var(--text-primary)"
                enabledBgColor="var(--positive-act)"
                disabledBgColor="var(--text-disabled)"
                isDisabled={
                  changePasswordError ||
                  passwordError ||
                  !(
                    passwordValues?.confirmPasswordValue?.length >
                      GlobalConfiguration.lengthCheckZero &&
                    passwordValues?.newPasswordValue?.length >
                      GlobalConfiguration.lengthCheckTwo &&
                    passwordValues?.confirmPasswordValue?.length >
                      GlobalConfiguration.lengthCheckZero
                  )
                }
                handleFunction={(e) => {
                  handleConfirmChangePassword(e);
                }}
                isLoaderNeeded={true}
                isLoading={buttonLoading}
                isFillNeeded={true}
                inActiveVariantNeeded={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
